// srt-common-compliance.js
// Validation functions for tyre limits, front axle loads, rear axle loads,
// and axle group rules for SRT calculation compliance.
import { VEHICLE_TYPE_RULES } from './srt-common-presets.js';
import { mapTyreConfigCode, getAxleGroupLabel, tyreConfigToIndex } from './srt-common-ui-map.js';

const AXLE_GROUP_LIMITS = {
   front: {
      // Steer axles
      truck: {
         single: {
            single: { standard: 6000, abs: 6000 },
            wide: { standard: 7000, abs: 6000 },
         },
         tandem: {
            // twin steer
            any: { standard: 10000, abs: 10000 },
         },
      },
      // vehicle:numaxles:tyreconfig
      default: {
         single: {
            single: { standard: 6000, abs: 6000 },
            wide: { standard: 7000, abs: 7000 },
            dual: { standard: 10000, abs: 10000 },
         },
         tandem: {
            single: { standard: 11000, abs: 11000 },
            wide: { standard: 14000, abs: 14000 },
            dual: { standard: 16500, abs: 16500 },
         },
         tridem: {
            single: { standard: 15000, abs: 15000 },
            wide: { standard: 20000, abs: 20000 },
            dual: { standard: 20000, abs: 20000 },
         },
         quad: {
            single: { standard: 15000, abs: 15000 },
            wide: { standard: 20000, abs: 20000 },
            dual: { standard: 20000, abs: 20000 },
         },
      },
   },

   rear: {
      'pig-trailer': {
         single: {
            any: { standard: 9000, abs: 9000 },
         },
         tandem: {
            single: { standard: 11000, abs: 11000 },
            wide: { standard: 14000, abs: 14000 },
            any: { standard: 16500, abs: 16500 },
         },
         tridem: {
            any: { standard: 18000, abs: 18000 },
         },
         quad: {
            single: { standard: 15000, abs: 15000 },
            wide: { standard: 20000, abs: 20000 },
            dual: { standard: 20000, abs: 20000 },
         },
      },
      'low-loader': {
         // per axle limits
         any: {
            single: (axleCount) => ({ standard: 4500 * axleCount, abs: 4500 * axleCount }),
            wide: (axleCount) => ({ standard: 4500 * axleCount, abs: 4500 * axleCount }),
            dual: (axleCount) => ({ standard: 9000 * axleCount, abs: 9000 * axleCount }),
            'row-of-eight': (axleCount) => ({ standard: 18000 * axleCount, abs: 18000 * axleCount }),
         },
      },
      // vehicle:numaxles:tyreconfig
      default: {
         single: {
            single: { standard: 6000, abs: 6000 },
            wide: { standard: 7000, abs: 7000 },
            dual: { standard: 10000, abs: 10000 },
         },
         tandem: {
            single: { standard: 11000, abs: 11000 },
            wide: { standard: 14000, abs: 14000 },
            dual: { standard: 16500, abs: 16500 },
         },
         tridem: {
            single: { standard: 15000, abs: 15000 },
            wide: { standard: 20000, abs: 20000 },
            dual: { standard: 20000, abs: 20000 },
         },
         quad: {
            single: { standard: 15000, abs: 15000 },
            wide: { standard: 20000, abs: 20000 },
            dual: { standard: 20000, abs: 20000 },
         },
      },
   },
};

/*
 * Validates axle limits based on vehicle type, axle counts, tyre configurations, and gross mass.
 * Tyre sizes are not used to determine limits, only tyre configurations.
 *
 * Returns an object with dataWarning and reason if limits are exceeded.
 * @param {Object} inputData - Input data containing vehicle type, axle counts, tyre configurations, and gross mass.
 * @returns {Object} - { dataWarning: boolean, reason: string }
 */

export function checkAxleLimits(inputData) {
   const { vehicleType, frontAxles, rearAxles, tyreConfigs, fGrossMass, rGrossMass } = inputData;
   let dataWarning = false;
   let reason = '';

   // 1. Extract tyre config labels for front and rear axles
   const tyreConfigLabels = extractTyreConfigGroupLabels(tyreConfigs, frontAxles, rearAxles);
   //console.log('Extracted tyre config labels:', tyreConfigLabels);
   const { front: frontTyreConfig, rear: rearTyreConfig } = tyreConfigLabels;

   // 2. Check front limits
   if (frontAxles > 0) {
      const frontLimits = checkAxleGroupLimits('front', vehicleType, frontAxles, frontTyreConfig, fGrossMass);
      //console.log('Front axle limits:', frontLimits);
      if (frontLimits.isOverLimit) {
         dataWarning = true;
         const groupLabel = getAxleGroupLabel(frontAxles);
         reason += `Front axle group (${groupLabel} group, ${frontTyreConfig} tyres) gross mass may exceed allowable loading of ${frontLimits.standardLimit} kg. `;
         //reason += `Front axle group,may exceed standard limit of ${frontLimits.standardLimit} kg. `;
      }
   }

   // 3. Check rear limits
   const rearLimits = checkAxleGroupLimits('rear', vehicleType, rearAxles, rearTyreConfig, rGrossMass);
   //console.log('Rear axle limits:', rearLimits);
   if (rearLimits.isOverLimit) {
      dataWarning = true;
      const groupLabel = getAxleGroupLabel(rearAxles);
      reason += `Rear axle group (${groupLabel} group, ${rearTyreConfig} tyres) gross mass may exceed allowable loading of ${rearLimits.standardLimit} kg. `;
      //reason += `Rear axle group may exceed standard limit of ${rearLimits.standardLimit} kg. `;
   }
   // if (dataWarning && reason.length > 0) {
   //    console.log(`⚠️ Axle limits warning: ${reason}`);
   // }

   return { dataWarning, reason };
}

/**
 * Extracts uniform tyre config labels ('single' | 'wide' | 'dual' | 'row-of-eight') for front and rear axle groups.
 * Warns if any group has mixed configurations.
 *
 * @param {number[]} tyreConfigs - Tyre config codes (e.g. [0, 1, 1, 1]) -  array of all axles
 * @param {number} frontAxles - Number of front axles in front group
 * @param {number} rearAxles - Number of rear axles in rear group
 * @returns {{ front: string|null, rear: string }}
 */
function extractTyreConfigGroupLabels(tyreConfigs, frontAxles, rearAxles) {
   const expectedTotal = frontAxles + rearAxles;
   if (tyreConfigs.length !== expectedTotal) {
      console.warn(
         `⚠️ tyreConfigs length (${tyreConfigs.length}) does not match front + rear axle count (${expectedTotal})`
      );
   }

   const frontArray = frontAxles > 0 ? tyreConfigs.slice(0, frontAxles) : [];
   const rearArray = tyreConfigs.slice(frontAxles, frontAxles + rearAxles);

   const unique = (arr) => [...new Set(arr)];

   const getLabel = (arr, label) => {
      const distinct = unique(arr);
      if (distinct.length > 1) {
         // We assume that the tyre config codes are consistent within each axle group - warn if not
         console.warn(`⚠️ Tyre config mismatch in ${label} axles: [${arr.join(', ')}]`);
      }
      const code = distinct[0];
      return mapTyreConfigCode(code) ?? 'unknown';
   };

   return {
      front: frontAxles > 0 ? getLabel(frontArray, 'front') : null,
      rear: getLabel(rearArray, 'rear'),
   };
}

/**
 * Checks axle group limits based on vehicle type, axle count, tyre config label, and gross mass.
 *
 * @param {string} axlePosition - 'front' or 'rear'
 * @param {string} vehicleType - Vehicle type (e.g. 'truck', 'dog-trailer', etc.)
 * @param {number} axleCount - Number of axles in the group
 * @param {string} tyreConfigLabel - Tyre configuration label ('single', 'wide', 'dual', 'row-of-eight')
 * @param {number} grossMassKg - Gross mass in kg
 * @returns {{ standardLimit: number|null, absLimit: number|null, isOverLimit: boolean, warning: string|null }}
 */
export function checkAxleGroupLimits(axlePosition, vehicleType, axleCount, tyreConfigLabel, grossMassKg) {
   const axleGroup = getAxleGroupLabel(axleCount);

   const positionTable = AXLE_GROUP_LIMITS[axlePosition] || {}; // front or rear
   const vtypeSection = positionTable[vehicleType] || positionTable['default'] || {}; // vehicle type or default
   const groupSection = vtypeSection[axleGroup] || vtypeSection['any'] || {}; // axles group type (number of axles) or any

   let limits = groupSection[tyreConfigLabel] || groupSection['any'];

   if (typeof limits === 'function') {
      limits = limits(axleCount);
   }

   if (!limits) {
      console.warn(
         `⚠️ No axle group limits found for ${axlePosition} ${vehicleType} ${axleGroup} (${tyreConfigLabel})`
      );
      return {
         standardLimit: null,
         absLimit: null,
         isOverLimit: false,
         warning: `No limit found for ${vehicleType}, ${axleGroup}, ${tyreConfigLabel}`,
      };
   }

   const isOverLimit = grossMassKg > limits.standard;
   const warning = isOverLimit ? 'Entered mass exceeds allowable standard limit' : null;

   // console.log(
   //    `Axle group check: ${axlePosition} ${vehicleType} ${axleGroup} (${tyreConfigLabel}) - Gross: ${grossMassKg} kg, Limit: ${limits.standard} kg`
   // );

   return {
      standardLimit: limits.standard,
      absLimit: limits.abs,
      isOverLimit,
      warning,
   };
}
/**
 * Returns the max allowable gross mass (standard and absolute) for front and rear axle groups based on mass limits.
 * @param {Object} params
 * @param {string} params.vehicleType - e.g. 'truck', 'semi-trailer'
 * @param {number} params.frontAxles
 * @param {number} params.rearAxles
 * @param {string} params.frontConfig - 'single' | 'wide' | 'dual' | 'any'
 * @param {string} params.rearConfig
 * @returns {Object} { front: { standard, abs }, rear: { standard, abs } }
 */
export function getGML({ vehicleType, frontAxles, rearAxles, frontConfig, rearConfig }) {
   const vehicleRules = VEHICLE_TYPE_RULES[vehicleType];

   if (!vehicleRules) {
      throw new Error(`Invalid vehicle type: ${vehicleType}`);
   }

   if (frontAxles < vehicleRules.front.min || frontAxles > vehicleRules.front.max) {
      throw new Error(
         `Invalid number of front axles for ${vehicleType}. Min: ${vehicleRules.front.min}, Max: ${vehicleRules.front.max}`
      );
   }

   if (rearAxles < vehicleRules.rear.min || rearAxles > vehicleRules.rear.max) {
      throw new Error(
         `Invalid number of rear axles for ${vehicleType}. Min: ${vehicleRules.rear.min}, Max: ${vehicleRules.rear.max}`
      );
   }

   // --- Front group limit lookup ---
   let frontLimits = { standard: 0, abs: 0 };

   if (frontAxles > 0) {
      const frontBase = AXLE_GROUP_LIMITS.front[vehicleType] || AXLE_GROUP_LIMITS.front.default;
      const fAxleLabel = getAxleGroupLabel(frontAxles);
      const frontAxleGroup = frontBase[fAxleLabel] || frontBase.any;

      if (frontAxleGroup) {
         frontLimits = frontAxleGroup[frontConfig] || frontAxleGroup.any || frontLimits;
      }
   }

   // --- Rear group limit lookup ---
   let rearLimits = { standard: 0, abs: 0 };

   if (rearAxles > 0) {
      const rearBase = AXLE_GROUP_LIMITS.rear[vehicleType] || AXLE_GROUP_LIMITS.rear.default;
      const rAxleLabel = getAxleGroupLabel(rearAxles);

      let rearAxleGroup = rearBase[rAxleLabel] || rearBase.any;
      if (rearAxleGroup) {
         if (typeof rearAxleGroup[rearConfig] === 'function') {
            // Case: low-loader, tyreConfig → function
            rearLimits = rearAxleGroup[rearConfig](rearAxles);
         } else {
            // Case: normal object lookup
            const limits = rearAxleGroup[rearConfig] || rearAxleGroup.any;
            rearLimits = limits || rearLimits;
         }
      }
   }

   return {
      front: frontLimits,
      rear: rearLimits,
   };
}
